import { useEffect, useRef, useState } from "react";
import type { ComponentProps } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { cn } from "@/lib/utils";
import {
  SEGMENTED_TRACK,
  segmentedOptionClass,
} from "@/components/common/segmented-filter/segmented-filter-styles";
import type { ProjectPlan, ProjectTag, RecordCreator } from "@/types/api";
import { CreatorPicker } from "@/components/common/creator-filter/creator-picker";
import { PlanPicker } from "@/components/common/plan-filter/plan-picker";
import {
  type DateRangeChip,
  getDateRangeForChip,
  isChipActive,
} from "@/lib/records/date-range-chips";
import {
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  useSidebarWidth,
} from "./use-sidebar-width";
import { CollapsibleSectionHeader } from "@/components/common/collapsible-section/collapsible-section-header";
import {
  COLLAPSED_BAR_WIDTH_CLASS,
  SidebarHeader,
  SidebarRail,
} from "@/components/common/collapsible-sidebar/collapsible-sidebar";
import { useCollapsedSections } from "@/components/common/collapsible-section/use-collapsed-sections";

interface RecordsSidebarProps {
  searchQuery?: string | null;
  onSearchChange?: (value: string | null) => void;
  tags?: ProjectTag[];
  selectedTags?: string[];
  onTagToggle?: (tagId: string) => void;
  showUntagged?: boolean;
  onUntaggedToggle?: () => void;
  plans?: ProjectPlan[];
  selectedPlanIds?: string[];
  onPlanToggle?: (planId: string) => void;
  dateFrom?: string | null;
  dateTo?: string | null;
  onDateFromChange?: (value: string | null) => void;
  onDateToChange?: (value: string | null) => void;
  creators?: RecordCreator[];
  selectedCreatorIds?: string[];
  onCreatorToggle?: (creatorId: string) => void;
  /**
   * Reports the sidebar's width (live while dragging), so a host can decide
   * how much room is left beside it — the Photos page uses it to fit the
   * record side panel. See [[records-view.spec]] US-006.
   */
  onWidthChange?: (width: number) => void;
  /**
   * Opt in to folding the whole sidebar away. The host owns the state: it says
   * whether the sidebar is collapsed and what a toggle does, and the sidebar
   * draws a "Filters" header with a collapse button, or the collapsed rail
   * (title turned on its side, badged with `activeCount` when filters are on).
   * Omit it and the sidebar renders exactly as before. See [[records-sidebar.spec]]
   * US-007 and [[collapsible-sidebar.spec]].
   */
  collapse?: {
    collapsed: boolean;
    onToggle: () => void;
    activeCount?: number;
  };
}

const DATE_CHIPS: DateRangeChip[] = ["24H", "7D", "30D"];

/** Per-browser, shared by every host — same scope as the sidebar width. */
const COLLAPSED_STORAGE_KEY = "otus:records-sidebar-collapsed";

type SectionId = "date" | "creators" | "tags" | "plans";

type SectionHeaderProps = Omit<
  ComponentProps<typeof CollapsibleSectionHeader>,
  "controlsId" | "onToggle"
> & {
  section: SectionId;
  onToggle: (section: SectionId) => void;
};

/** Collapsible title for one sidebar section. See spec US-006. */
function SectionHeader({
  section,
  onToggle,
  className,
  ...props
}: SectionHeaderProps) {
  return (
    <CollapsibleSectionHeader
      {...props}
      controlsId={`records-sidebar-${section}`}
      onToggle={() => onToggle(section)}
      className={cn("px-5", className)}
    />
  );
}

export function RecordsSidebar({
  searchQuery = null,
  onSearchChange,
  tags,
  selectedTags = [],
  onTagToggle,
  showUntagged = false,
  onUntaggedToggle,
  plans,
  selectedPlanIds = [],
  onPlanToggle,
  dateFrom = null,
  dateTo = null,
  onDateFromChange,
  onDateToChange,
  creators,
  selectedCreatorIds = [],
  onCreatorToggle,
  onWidthChange,
  collapse,
}: RecordsSidebarProps) {
  const { width, isResizing, handleProps } = useSidebarWidth();
  const onWidthChangeRef = useRef(onWidthChange);
  useEffect(() => {
    onWidthChangeRef.current = onWidthChange;
  });
  useEffect(() => {
    onWidthChangeRef.current?.(width);
  }, [width]);
  const { isCollapsed, toggle } = useCollapsedSections(COLLAPSED_STORAGE_KEY);
  const [searchInput, setSearchInput] = useState(searchQuery ?? "");
  const [searchInputSource, setSearchInputSource] = useState(searchQuery);

  // Mirror externally controlled `searchQuery` into the local input by
  // adjusting state during render rather than via an effect — keeps the
  // committed paint in sync with the prop without a flash of stale text.
  if (searchInputSource !== searchQuery) {
    setSearchInputSource(searchQuery);
    setSearchInput(searchQuery ?? "");
  }

  const showDateSection = !!onDateFromChange && !!onDateToChange;
  const showTagsSection = !!tags && !!onTagToggle;
  // Hidden when the project has one or fewer plans — nothing to filter by.
  const showPlansSection =
    !!plans && plans.length > 1 && !!onPlanToggle;
  // Shown whenever the project has at least one creator. Hidden only on an
  // empty creators list — e.g. the public share page, which intentionally
  // passes no creators (no public endpoint to list contributors).
  const showCreatorSection =
    !!creators && creators.length > 0 && !!onCreatorToggle;

  const hasDateFilter = !!dateFrom || !!dateTo;
  const activeTagCount = selectedTags.length + (showUntagged ? 1 : 0);
  const dateCollapsed = isCollapsed("date");
  const creatorsCollapsed = isCollapsed("creators");
  const tagsCollapsed = isCollapsed("tags");
  const plansCollapsed = isCollapsed("plans");

  // Folded away: a slim rail in place of the whole sidebar. (Every hook above
  // has already run, so the width and section state survive the fold.)
  if (collapse?.collapsed) {
    return (
      <aside
        aria-label="Filters"
        data-collapsed="true"
        className={cn(
          "hidden shrink-0 border-r border-border bg-panel md:flex",
          COLLAPSED_BAR_WIDTH_CLASS,
        )}
      >
        <SidebarRail
          title="Filters"
          expandLabel="Show filters"
          onExpand={collapse.onToggle}
          badge={collapse.activeCount}
        />
      </aside>
    );
  }

  return (
    <aside
      aria-label="Filters"
      style={{ width }}
      className="relative hidden shrink-0 flex-col border-r border-border bg-panel md:flex"
    >
      {collapse && (
        <SidebarHeader
          title="Filters"
          collapseLabel="Hide filters"
          onCollapse={collapse.onToggle}
        />
      )}
      {/* Sections above Plans share one shrinkable, self-scrolling block so
          the Plans section below can flex-fill the rest. See spec US-005. */}
      <div className="flex min-h-0 flex-col overflow-y-auto">
        {onSearchChange && (
          <div className="px-3 py-3 border-b border-border">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-2">
              Search
            </h3>
            <form
              className="px-2"
              onSubmit={(e) => {
                e.preventDefault();
                const trimmed = searchInput.trim();
                onSearchChange(trimmed || null);
              }}
            >
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Describe what you're looking for…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-8 pr-8 h-8 text-sm"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput("");
                      onSearchChange(null);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {showDateSection && (
          <div className="border-b border-border py-3">
            <SectionHeader
              section="date"
              title="Date Range"
              collapsed={dateCollapsed}
              onToggle={toggle}
              activeCount={hasDateFilter ? 1 : 0}
              action={
                hasDateFilter && (
                  <button
                    type="button"
                    onClick={() => {
                      onDateFromChange!(null);
                      onDateToChange!(null);
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground cursor-pointer"
                    aria-label="Clear date range"
                  >
                    Clear
                  </button>
                )
              }
            />
            {!dateCollapsed && (
              <div id="records-sidebar-date" className="mt-2 px-5">
                {/* One track, the chosen chip the raised segment: the same look as
                    the Issues status row, not solid blue pills. They are pressed
                    buttons, not tabs, because tapping the chosen one clears it.
                    See [[segmented-filter.spec]]. */}
                <div
                  role="group"
                  aria-label="Date range"
                  className={cn(SEGMENTED_TRACK, "mb-2")}
                >
                  {DATE_CHIPS.map((chip) => {
                    const active = isChipActive(chip, dateFrom, dateTo);
                    return (
                      <button
                        key={chip}
                        type="button"
                        onClick={() => {
                          if (active) {
                            onDateFromChange!(null);
                            onDateToChange!(null);
                            return;
                          }
                          const range = getDateRangeForChip(chip);
                          onDateFromChange!(range.from);
                          onDateToChange!(range.to);
                        }}
                        aria-pressed={active}
                        className={cn(
                          segmentedOptionClass(active),
                          "flex-1 justify-center py-1 text-xs",
                        )}
                      >
                        {chip}
                      </button>
                    );
                  })}
                </div>
                <DateRangePicker
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  onDateFromChange={onDateFromChange!}
                  onDateToChange={onDateToChange!}
                />
              </div>
            )}
          </div>
        )}

        {showCreatorSection && (
          <div className="border-b border-border py-3">
            <SectionHeader
              section="creators"
              title="Created by"
              collapsed={creatorsCollapsed}
              onToggle={toggle}
              activeCount={selectedCreatorIds.length}
            />
            {!creatorsCollapsed && (
              <div id="records-sidebar-creators" className="mt-1">
                <CreatorPicker
                  creators={creators!}
                  selectedIds={selectedCreatorIds}
                  onToggle={onCreatorToggle!}
                  className="max-h-64"
                />
              </div>
            )}
          </div>
        )}

        {showTagsSection && (
          <div className="border-b border-border py-3">
            <SectionHeader
              section="tags"
              title="Tags"
              collapsed={tagsCollapsed}
              onToggle={toggle}
              activeCount={activeTagCount}
            />
            {!tagsCollapsed && (
              <div
                id="records-sidebar-tags"
                className="mt-2 flex flex-wrap gap-1.5 px-5"
              >
                {onUntaggedToggle && (
                  <Badge
                    variant={showUntagged ? "default" : "outline"}
                    onClick={onUntaggedToggle}
                    className={cn(
                      "cursor-pointer select-none text-xs py-1 px-2",
                      showUntagged && "bg-tertiary text-tertiary-foreground",
                    )}
                  >
                    Untagged
                  </Badge>
                )}
                {tags!.map((tag) => {
                  const selected = selectedTags.includes(tag.id);
                  return (
                    <Badge
                      key={tag.id}
                      variant={selected ? "default" : "outline"}
                      onClick={() => onTagToggle!(tag.id)}
                      className={cn(
                        "cursor-pointer select-none text-xs py-1 px-2",
                        selected && "bg-tertiary text-tertiary-foreground",
                      )}
                    >
                      {tag.name}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Last section — grows to the sidebar's bottom edge, with a floor so a
          short viewport shrinks the block above instead of this list. */}
      {showPlansSection && (
        <div
          className={cn(
            "flex flex-col overflow-hidden py-3",
            // Collapsed, it is only a title — no height floor, no flex-fill.
            !plansCollapsed && "min-h-48 flex-1",
          )}
        >
          <SectionHeader
            section="plans"
            title="Plans"
            collapsed={plansCollapsed}
            onToggle={toggle}
            activeCount={selectedPlanIds.length}
            className="shrink-0"
          />
          {!plansCollapsed && (
            <div
              id="records-sidebar-plans"
              className="mt-1 flex min-h-0 flex-1 flex-col"
            >
              <PlanPicker
                plans={plans!}
                selectedIds={selectedPlanIds}
                onToggle={onPlanToggle!}
                className="min-h-0 flex-1"
              />
            </div>
          )}
        </div>
      )}

      {/* Drag-to-resize handle straddling the right border. See spec US-004. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        aria-valuenow={width}
        aria-valuemin={SIDEBAR_MIN_WIDTH}
        aria-valuemax={SIDEBAR_MAX_WIDTH}
        tabIndex={0}
        {...handleProps}
        className={cn(
          "absolute inset-y-0 -right-1 z-10 w-2 touch-none cursor-col-resize",
          "after:absolute after:inset-y-0 after:left-1/2 after:w-0.5 after:-translate-x-1/2 after:transition-colors",
          "focus-visible:outline-none hover:after:bg-tertiary focus-visible:after:bg-tertiary",
          isResizing && "after:bg-tertiary",
        )}
      />
    </aside>
  );
}
